import React, { useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { inputCls, BUDGET_OPTIONS } from "@/components/EnquiryForm";
import { reportContactConversion } from "@/lib/googleAds";

const API = "/api";

const MARKET_OPTIONS = [
  "Dubai",
  "Abu Dhabi",
  "Ras Al Khaimah & Al Marjan Island",
  "Land & JV",
  "Not sure yet",
];

const BUYER_OPTIONS = [
  "Second Home Buyer",
  "Investment Buyer",
  "Golden Visa Buyer",
  "Land & JV Investor",
  "Developer",
  "Family Office",
  "Not Sure Yet",
];

const ConsultationForm = () => {
  const [form, setForm] = useState({
    full_name: "",
    whatsapp: "",
    email: "",
    preferred_market: "",
    budget_range: "",
    buyer_type: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const conversionReported = useRef(false);
  const submissionInFlight = useRef(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (submissionInFlight.current || done) return;
    submissionInFlight.current = true;
    setLoading(true);
    try {
      await axios.post(`${API}/consultations`, form);
      if (!conversionReported.current) {
        conversionReported.current = true;
        reportContactConversion();
      }
      setDone(true);
      toast.success("Consultation request received. We will be in touch personally.");
    } catch (err) {
      toast.error("Something went wrong. Please try again or reach us on WhatsApp.");
    } finally {
      submissionInFlight.current = false;
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div className="bg-[#13151D] border border-[rgba(197,168,128,0.3)] rounded-2xl p-10 text-center" data-testid="consultation-success">
        <p className="font-display text-3xl text-[#E6CA65] mb-4">Your consultation is requested</p>
        <p className="text-[#9DA3AE] font-light max-w-md mx-auto">
          Thank you, {form.full_name.split(" ")[0]}. A member of the ACRE advisory team will contact you personally on
          WhatsApp to arrange your private consultation.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="bg-[#13151D] border border-[rgba(197,168,128,0.25)] rounded-2xl p-7 sm:p-9 space-y-5" data-testid="consultation-form">
      <div className="grid sm:grid-cols-2 gap-5">
        <div>
          <label htmlFor="cf-name" className="text-xs text-[#9DA3AE] block mb-1.5">Full name *</label>
          <input id="cf-name" data-testid="consultation-name-input" required className={inputCls} value={form.full_name} onChange={set("full_name")} placeholder="Your full name" />
        </div>
        <div>
          <label htmlFor="cf-whatsapp" className="text-xs text-[#9DA3AE] block mb-1.5">WhatsApp number (with country code) *</label>
          <input id="cf-whatsapp" data-testid="consultation-whatsapp-input" required className={inputCls} value={form.whatsapp} onChange={set("whatsapp")} placeholder="+44 ..." />
        </div>
      </div>
      <div>
        <label htmlFor="cf-email" className="text-xs text-[#9DA3AE] block mb-1.5">Email *</label>
        <input id="cf-email" data-testid="consultation-email-input" required type="email" className={inputCls} value={form.email} onChange={set("email")} placeholder="you@example.com" />
      </div>
      <div>
        <label htmlFor="cf-market" className="text-xs text-[#9DA3AE] block mb-1.5">Preferred market *</label>
        <select id="cf-market" data-testid="consultation-market-select" required className={inputCls} value={form.preferred_market} onChange={set("preferred_market")}>
          <option value="" disabled>Select a market</option>
          {MARKET_OPTIONS.map((o) => (
            <option key={o} value={o}>{o}</option>
          ))}
        </select>
      </div>
      <div className="grid sm:grid-cols-2 gap-5">
        <div>
          <label htmlFor="cf-budget" className="text-xs text-[#9DA3AE] block mb-1.5">Budget range (optional)</label>
          <select id="cf-budget" data-testid="consultation-budget-select" className={inputCls} value={form.budget_range} onChange={set("budget_range")}>
            <option value="">Select a range</option>
            {BUDGET_OPTIONS.map((o) => (
              <option key={o} value={o}>{o}</option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="cf-buyer" className="text-xs text-[#9DA3AE] block mb-1.5">Buyer type (optional)</label>
          <select id="cf-buyer" data-testid="consultation-buyer-type-select" className={inputCls} value={form.buyer_type} onChange={set("buyer_type")}>
            <option value="">Select buyer type</option>
            {BUYER_OPTIONS.map((o) => (
              <option key={o} value={o}>{o}</option>
            ))}
          </select>
        </div>
      </div>
      <div>
        <label htmlFor="cf-message" className="text-xs text-[#9DA3AE] block mb-1.5">Message (optional)</label>
        <textarea id="cf-message" data-testid="consultation-message-input" rows={4} className={inputCls} value={form.message} onChange={set("message")} placeholder="Tell us about your plans, timeline, or anything on your mind." />
      </div>
      <button
        type="submit"
        disabled={loading}
        data-testid="consultation-submit-button"
        className="w-full text-sm uppercase tracking-[0.14em] font-semibold px-6 py-4 rounded-full text-[#0B0C10] bg-gradient-to-r from-[#E6CA65] via-[#C5A880] to-[#9A7B4F] hover:shadow-[0_0_24px_rgba(197,168,128,0.45)] transition-shadow duration-300 disabled:opacity-60"
      >
        {loading ? "Sending..." : "Book a Private ACRE Consultation"}
      </button>
      <p className="text-[11px] text-[#6b7280] text-center font-light">
        Your details are shared only with the ACRE advisory team. No obligation, no pressure.
      </p>
    </form>
  );
};

export default ConsultationForm;
